import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Layers, Copy as CopyIcon, ArrowRight, CheckCircle2, Search, RotateCw, X, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { DualPicker, PickerItem, PickerFetchResp } from '../components/DualPicker';
import { Combobox } from '../components/Combobox';
import MultiSelectDialog, { MultiSelectItem } from '../components/MultiSelectDialog';
import MachineDetailModal from '../components/MachineDetailModal';
import { CategoryPicker } from './SimilariSection';
import { CatEscluseBadge } from '../components/CatEscluseBadge';
import { useDebounce } from '../../../../hooks/useDebounce';
import {
  fetchMachines, fetchArticles, fetchMarche, fetchCategorie, fetchTipologie,
  createAssociations, fetchMatrix, copyAssociations, fetchArticlesBySkus,
  fetchRelationEscluse,
  MarcaOption, CategoriaOption, CellState, ArticleLite, MachineRow,
} from '../api';

type Tab = 'dual' | 'matrix' | 'copy';

export default function AbbinamentoSection() {
  const [tab, setTab] = useState<Tab>('dual');

  return (
    <div className="h-full flex flex-col" data-testid="abbinamento-section">
      <div className="flex-none mb-4">
        <h1 className="text-[22px] font-semibold text-[#1f1f1d] mb-2">Abbinamento</h1>
        <div className="flex gap-1 border-b border-[#e4e4e1]" role="tablist">
          {([
            { id: 'dual',   label: 'Tabella', icon: Layers,   desc: 'Selezione articoli + macchine' },
            { id: 'copy',   label: 'Copia',    icon: CopyIcon, desc: 'Sorgente → destinazioni' },
          ] as { id: Tab; label: string; icon: React.ComponentType<{ className?: string }>; desc: string }[]).map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                role="tab"
                data-testid={`tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 -mb-px border-b-2 transition-colors ${
                  active
                    ? 'border-[#2b3a8f] text-[#2b3a8f]'
                    : 'border-transparent text-[#6d6d68] hover:text-[#3a3a36]'
                }`}
                title={t.desc}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="text-[15px] font-semibold">{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        {tab === 'dual' && <DualTab />}
        {tab === 'matrix' && <MatrixTab />}
        {tab === 'copy' && <CopyTab />}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// DUAL TAB
// ════════════════════════════════════════════════════════════════════

/** Macchina → riga del picker, con le figlie già risolte in sottorighe.
 *  Niente tipologia sulla riga: la si filtra dal menu in testa al pannello,
 *  ripeterla su ogni riga rubava solo spazio a modello e marca. */
const toPickerItem = (m: MachineRow): PickerItem => ({
  id: m.id,
  primary: m.model,
  secondary: m.brand,
  kindLabel: m.kind === 'similar' ? 'similare' : m.kind === 'rebrand' ? 'rebrand' : null,
  children: m.children?.map(toPickerItem),
});

function DualTab() {
  const [machineIds, setMachineIds] = useState<Set<string>>(new Set());
  const [skus, setSkus] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  // Liste complete: servono solo alla scheda macchina (modifica marca e
  // tipologia). I filtri dei due picker si alimentano da soli, in cascata.
  const [marche, setMarche] = useState<MarcaOption[]>([]);
  const [tipologie, setTipologie] = useState<{ code: string; label: string }[]>([]);

  useEffect(() => {
    fetchMarche().then(setMarche).catch(() => {});
    fetchTipologie().then(setTipologie).catch(() => {});
  }, []);

  // Le macchine toccate dall'ultimo caricamento (padri + figlie), per aprire
  // la scheda modello senza rileggerle dal server.
  const machineById = useRef<Map<string, MachineRow>>(new Map());
  const [detailMachine, setDetailMachine] = useState<MachineRow | null>(null);

  const machinesFetcher = useMemo(() => (
    (params: { search?: string; brand?: string; category?: string; page: number; pageSize: number }) =>
      fetchMachines({
        search: params.search, brand: params.brand, tipo: params.category,
        withChildren: true,
        page: params.page, pageSize: params.pageSize,
      }).then((r): PickerFetchResp => {
        if (params.page === 0) machineById.current.clear();
        r.items.forEach(m => {
          machineById.current.set(m.id, m);
          m.children?.forEach(c => machineById.current.set(c.id, c));
        });
        return { items: r.items.map(toPickerItem), total: r.total };
      })
  ), []);

  const articlesFetcher = useMemo(() => (
    (params: { search?: string; brand?: string; category?: string; page: number; pageSize: number }) =>
      fetchArticles({
        search: params.search, brand: params.brand, category: params.category,
        page: params.page, pageSize: params.pageSize,
      }).then((r): PickerFetchResp => ({
        items: r.items.map((a): PickerItem => ({
          id: a.sku, primary: a.sku, secondary: a.desc, meta: a.cat,
        })),
        total: r.total,
      }))
  ), []);

  const expectedAssoc = machineIds.size * skus.size;

  const onAssociate = async () => {
    if (expectedAssoc === 0) return;
    setSubmitting(true);
    try {
      const r = await createAssociations({
        skus: Array.from(skus),
        machineIds: Array.from(machineIds),
      });
      toast.success(
        `${r.created} associazioni create` +
        (r.skipped_existing ? ` · ${r.skipped_existing} già esistenti` : '') +
        (r.skipped_excluded ? ` · ${r.skipped_excluded} saltate per categorie escluse` : ''),
      );
      setMachineIds(new Set()); setSkus(new Set());
    } catch (e: any) {
      toast.error('Errore: ' + (e?.message ?? 'unknown'));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleM = (id: string) => {
    const s = new Set(machineIds); s.has(id) ? s.delete(id) : s.add(id); setMachineIds(s);
  };
  const toggleS = (sku: string) => {
    const s = new Set(skus); s.has(sku) ? s.delete(sku) : s.add(sku); setSkus(s);
  };
  const toggleManyM = (ids: string[], select: boolean) => {
    const s = new Set(machineIds);
    ids.forEach(id => select ? s.add(id) : s.delete(id));
    setMachineIds(s);
  };
  const toggleManyS = (ids: string[], select: boolean) => {
    const s = new Set(skus);
    ids.forEach(id => select ? s.add(id) : s.delete(id));
    setSkus(s);
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3">
      <div className="flex-1 min-h-0 grid grid-cols-2 gap-3.5">
        <DualPicker
          title="Articoli"
          testId="dual-articles"
          selected={skus}
          onToggle={toggleS}
          onToggleMany={toggleManyS}
          onClear={() => setSkus(new Set())}
          facetDomain="articles"
          brandLabel="le marche"
          secondaryLabel="le categorie"
          searchPlaceholder="Cerca SKU o descrizione…"
          fetcher={articlesFetcher}
        />
        <DualPicker
          title="Macchine"
          testId="dual-machines"
          selected={machineIds}
          onToggle={toggleM}
          onToggleMany={toggleManyM}
          onClear={() => setMachineIds(new Set())}
          facetDomain="machines"
          brandLabel="le marche"
          secondaryLabel="le tipologie"
          searchPlaceholder="Cerca modello…"
          fetcher={machinesFetcher}
          onOpenDetail={it => {
            const m = machineById.current.get(it.id);
            if (m) setDetailMachine(m);
          }}
          detailTitle="Vedi dettaglio macchina"
        />
      </div>

      <div className="flex-none px-4 py-3 bg-[#fafbff] border border-[#d4dcf7] rounded-[10px] flex items-center gap-4" data-testid="dual-actionbar">
        <div className="text-[15px] text-[#3a3a36]">
          <strong className="text-[#2b3a8f]">{machineIds.size}</strong> macchine
          <span className="mx-1.5 text-[#5c5c57]">×</span>
          <strong className="text-[#2b3a8f]">{skus.size}</strong> articoli
          <span className="mx-1.5 text-[#5c5c57]">=</span>
          <strong className="text-[#1f1f1d]">{expectedAssoc.toLocaleString('it')}</strong>
          <span className="ml-1 text-[14px] text-[#6d6d68]">associazioni</span>
        </div>
        <div className="flex-1" />
        <button
          onClick={onAssociate}
          disabled={expectedAssoc === 0 || submitting}
          data-testid="associate-btn"
          className="px-4 py-2 rounded-[8px] bg-[#2b3a8f] text-white text-[15px] font-semibold hover:bg-[#243279] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
        >
          {submitting ? 'Salvataggio…' : <>Associa <ArrowRight className="w-3.5 h-3.5" /></>}
        </button>
      </div>

      {detailMachine && (
        <MachineDetailModal
          machine={detailMachine}
          onClose={() => setDetailMachine(null)}
          marche={marche}
          tipologie={tipologie}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// MATRIX TAB — articolo×macchina con combobox di ricerca + conferma
// ════════════════════════════════════════════════════════════════════

type Pending = 'add' | 'remove' | null;

// ─────────────────────────────────────────────────────────────────────
// Stile griglia matrice V2: bordi più scuri + zebra-stripe per colonna.
// Per ripristinare la versione precedente, impostare ``false``.
// ─────────────────────────────────────────────────────────────────────
const MATRIX_GRID_V2 = true;

function MatrixTab() {
  const [skus, setSkus] = useState<string[]>([]);
  const [skuMeta, setSkuMeta] = useState<Record<string, ArticleLite>>({});
  const [machineIds, setMachineIds] = useState<string[]>([]);
  const [machineMeta, setMachineMeta] = useState<Record<string, MachineRow>>({});
  const [matrix, setMatrix] = useState<Record<string, Record<string, CellState>>>({});
  const [pending, setPending] = useState<Record<string, Record<string, Pending>>>({});
  const [loading, setLoading] = useState(false);
  const [marche, setMarche] = useState<MarcaOption[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Dialog multi-select
  const [addArticlesOpen, setAddArticlesOpen] = useState(false);
  const [addMachinesOpen, setAddMachinesOpen] = useState(false);
  const [addMachinesBrand, setAddMachinesBrand] = useState<string | null>(null);

  useEffect(() => { fetchMarche().then(setMarche).catch(() => {}); }, []);

  const reloadMatrix = async (s: string[], ids: string[]) => {
    if (!s.length || !ids.length) { setMatrix({}); return; }
    setLoading(true);
    try {
      const r = await fetchMatrix(s, ids);
      setMatrix(r.matrix);
      setPending({}); // clear pending on reload
    } finally {
      setLoading(false);
    }
  };

  const addSku = async (sku: string) => {
    const s = sku.trim().toUpperCase();
    if (!s || skus.includes(s)) return;
    const newSkus = [...skus, s];
    setSkus(newSkus);
    // arricchisci con meta (desc + brand)
    try {
      const items = await fetchArticlesBySkus([s]);
      const m = { ...skuMeta };
      items.forEach(it => { m[it.sku] = it; });
      setSkuMeta(m);
    } catch { /* ignora */ }
    if (machineIds.length) reloadMatrix(newSkus, machineIds);
  };

  const removeSku = (sku: string) => {
    const ns = skus.filter(x => x !== sku);
    setSkus(ns);
    if (machineIds.length) reloadMatrix(ns, machineIds);
  };

  // ── Aggiunta articoli alla matrice (via MultiSelectDialog) ──────
  const handleAddArticles = async (items: MultiSelectItem[]) => {
    const toAdd = items.filter(it => !skus.includes(it.id));
    if (!toAdd.length) { setAddArticlesOpen(false); return; }
    const merged = [...skus, ...toAdd.map(it => it.id)];
    // Popola subito skuMeta da quello che il dialog ha già caricato
    const m = { ...skuMeta };
    toAdd.forEach(it => {
      if (!m[it.id]) {
        m[it.id] = { sku: it.id, desc: it.primary, brand: it.brand, cat: it.secondary || '' };
      }
    });
    setSkuMeta(m);
    setSkus(merged);
    setAddArticlesOpen(false);
    if (machineIds.length) reloadMatrix(merged, machineIds);
    toast.success(`${toAdd.length} ${toAdd.length === 1 ? 'articolo aggiunto' : 'articoli aggiunti'} alla matrice`);
  };

  // ── Aggiunta macchine alla matrice (via MultiSelectDialog) ──────
  const handleAddMachines = async (items: MultiSelectItem[]) => {
    const toAdd = items.filter(it => !machineIds.includes(it.id));
    if (!toAdd.length) { setAddMachinesOpen(false); return; }
    const merged = [...machineIds, ...toAdd.map(it => it.id)];
    // Popola subito machineMeta dai dati che il dialog ha già
    const meta = { ...machineMeta };
    toAdd.forEach(it => {
      if (!meta[it.id]) {
        meta[it.id] = {
          id: it.id,
          model: it.primary,
          brand: it.brand,
          brandCode: null,
          type: it.secondary || null,
          series: null,
          compat: 0,
          lastAssoc: null,
          hasParent: false,
          rebrandFrom: null,
          rebrandModel: null,
        };
      }
    });
    setMachineMeta(meta);
    setMachineIds(merged);
    setAddMachinesOpen(false);
    if (skus.length) reloadMatrix(skus, merged);
    toast.success(`${toAdd.length} ${toAdd.length === 1 ? 'macchina aggiunta' : 'macchine aggiunte'} alla matrice`);
  };

  const toggleCell = (sku: string, mid: string) => {
    const current = matrix[sku]?.[mid] ?? 'none';
    if (current === 'inherited') {
      toast.info('Associazione ereditata: gestiscila da Macchine similari / Rebrand.');
      return;
    }
    setPending(p => {
      const row = { ...(p[sku] || {}) };
      const currPending = row[mid] ?? null;
      let next: Pending;
      if (current === 'direct') {
        next = currPending === 'remove' ? null : 'remove';
      } else {
        next = currPending === 'add' ? null : 'add';
      }
      if (next === null) {
        delete row[mid];
      } else {
        row[mid] = next;
      }
      const out = { ...p };
      if (Object.keys(row).length === 0) delete out[sku];
      else out[sku] = row;
      return out;
    });
  };

  const pendingList = useMemo(() => {
    const adds: { sku: string; mid: string }[] = [];
    const removes: { sku: string; mid: string }[] = [];
    for (const sku of Object.keys(pending)) {
      for (const mid of Object.keys(pending[sku])) {
        const v = pending[sku][mid];
        if (v === 'add') adds.push({ sku, mid });
        else if (v === 'remove') removes.push({ sku, mid });
      }
    }
    return { adds, removes, total: adds.length + removes.length };
  }, [pending]);

  const onConfirm = async () => {
    setSubmitting(true);
    try {
      // Le aggiunte per (mid, sku) le invio in batch a /associations
      // ma è semplice fare singole chiamate per i remove (cell endpoint).
      const { toggleMatrixCell } = await import('../api');
      const tasks: Promise<unknown>[] = [];
      for (const { sku, mid } of pendingList.adds) {
        tasks.push(toggleMatrixCell(sku, mid, 'direct'));
      }
      for (const { sku, mid } of pendingList.removes) {
        tasks.push(toggleMatrixCell(sku, mid, 'none'));
      }
      await Promise.all(tasks);
      toast.success(
        `${pendingList.adds.length} aggiunte · ${pendingList.removes.length} rimosse`,
      );
      setConfirmOpen(false);
      await reloadMatrix(skus, machineIds);
    } catch (e: any) {
      toast.error('Errore nel salvataggio: ' + (e?.message ?? 'unknown'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderSkuLabel = (sku: string) => {
    const m = skuMeta[sku];
    if (!m) return sku;
    const truncated = (m.desc || '').slice(0, 32) + ((m.desc?.length ?? 0) > 32 ? '…' : '');
    return `${sku} - ${truncated}`;
  };

  const skuTooltip = (sku: string) => {
    const m = skuMeta[sku];
    if (!m) return sku;
    return `${m.desc}${m.brand ? ' · ' + m.brand : ''}${m.cat ? ' · ' + m.cat : ''}`;
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3">
      {/* Setup */}
      <div className="flex-none bg-white border border-[#e2e2df] rounded-[10px] p-3.5 grid grid-cols-2 gap-3">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[13px] font-semibold text-[#5c5c57]">Articoli ({skus.length})</div>
            <button
              onClick={() => setAddArticlesOpen(true)}
              data-testid="matrix-add-articles-btn"
              className="px-3 py-1.5 rounded-[7px] bg-[#2b3a8f] text-white text-[13px] font-semibold hover:bg-[#243279] shadow-sm flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" /> Aggiungi articoli
            </button>
          </div>
          {skus.length === 0 ? (
            <div className="text-[13px] text-[#5c5c57] italic">Nessun articolo. Clicca "Aggiungi articoli" per iniziare.</div>
          ) : (
            <div className="flex flex-wrap gap-1 max-h-[70px] overflow-y-auto">
              {skus.map(s => (
                <span key={s} title={skuTooltip(s)}
                      className="text-[12px] font-mono px-1.5 py-0.5 bg-[#f3f3f0] rounded inline-flex items-center gap-1 max-w-[260px]">
                  <span className="truncate">{renderSkuLabel(s)}</span>
                  <button onClick={() => removeSku(s)} className="text-[#5c5c57] hover:text-[#8a3b3b] flex-none">×</button>
                </span>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[13px] font-semibold text-[#5c5c57]">Macchine ({machineIds.length})</div>
            <button
              onClick={() => setAddMachinesOpen(true)}
              data-testid="matrix-add-machines-btn"
              className="px-3 py-1.5 rounded-[7px] bg-[#2b3a8f] text-white text-[13px] font-semibold hover:bg-[#243279] shadow-sm flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" /> Aggiungi macchine
            </button>
          </div>
          {machineIds.length === 0 ? (
            <div className="text-[13px] text-[#5c5c57] italic">Nessuna macchina. Clicca "Aggiungi macchine" per iniziare.</div>
          ) : (
            <div className="flex flex-wrap gap-1 max-h-[70px] overflow-y-auto">
              {machineIds.map(mid => {
                const m = machineMeta[mid];
                return (
                  <span key={mid}
                        title={m ? `${m.brand} ${m.model}` : mid}
                        className="text-[12px] font-mono px-1.5 py-0.5 bg-[#f3f3f0] rounded inline-flex items-center gap-1 max-w-[200px]">
                    <span className="truncate">{m ? `${m.brand} ${m.model}` : mid.slice(-6)}</span>
                    <button
                      onClick={() => {
                        const ns = machineIds.filter(x => x !== mid);
                        setMachineIds(ns);
                        if (skus.length) reloadMatrix(skus, ns);
                      }}
                      className="text-[#5c5c57] hover:text-[#8a3b3b] flex-none"
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 min-h-0 bg-white border border-[#e2e2df] rounded-[10px] overflow-auto">
        {!skus.length || !machineIds.length ? (
          <div className="px-4 py-20 text-center text-[14px] text-[#5c5c57]">
            Cerca un articolo e carica una marca per costruire la matrice.
          </div>
        ) : loading ? (
          <div className="px-4 py-20 text-center text-[14px] text-[#5c5c57]">Caricamento…</div>
        ) : (
          <table className="text-[13px] border-separate" style={{ borderSpacing: 0 }}>
            <thead className="bg-[#fafafa]">
              <tr>
                <th className="px-2 py-2 sticky top-0 left-0 z-30 bg-[#fafafa] text-left font-mono font-bold tracking-wider text-[#5c5c57] text-[11px] min-w-[260px] border-b border-r border-[#e4e4e1]">ARTICOLO \ MACCHINA</th>
                {machineIds.map((mid, colIdx) => {
                  const m = machineMeta[mid];
                  const lbl = m ? m.model : mid.slice(-6);
                  const tip = m ? `${m.brand} ${m.model}`.trim() : mid;
                  const truncated = lbl.length > 18 ? lbl.slice(0, 18) + '…' : lbl;
                  const zebraHeaderBg = MATRIX_GRID_V2 && (colIdx % 2 === 1) ? 'bg-[#f0f0ed]' : 'bg-[#fafafa]';
                  const headerBorder = MATRIX_GRID_V2 ? 'border-b border-r border-[#d8d8d4]' : 'border-b border-[#e4e4e1]';
                  return (
                    <th key={mid} className={`px-1.5 py-2 font-semibold text-[14px] text-[#1f1f1d] whitespace-nowrap sticky top-0 z-10 ${zebraHeaderBg} ${headerBorder}`} title={tip}>
                      <div className="min-h-[84px] flex items-end justify-center" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                        {truncated}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {skus.map(sku => (
                <tr key={sku}>
                  <td className="px-2 py-1.5 sticky left-0 z-20 bg-white font-mono text-[13px] font-semibold text-[#3a3a36] max-w-[260px] truncate border-b border-r border-[#f1f1ee]" title={skuTooltip(sku)}>
                    {renderSkuLabel(sku)}
                  </td>
                  {machineIds.map((mid, colIdx) => {
                    const state = matrix[sku]?.[mid] ?? 'none';
                    const pend = pending[sku]?.[mid] ?? null;
                    // Zebra-stripe per colonna alternata (visibile solo sulle celle neutre).
                    const zebraBg = MATRIX_GRID_V2 && (colIdx % 2 === 1) ? 'bg-[#f7f7f5]' : 'bg-white';
                    const borderCls = MATRIX_GRID_V2 ? 'border border-[#d8d8d4]' : 'border';
                    // Stato effettivo dopo pending
                    let cls: string;
                    let icon: React.ReactNode = null;
                    if (state === 'inherited') {
                      cls = `bg-blue-100 ${MATRIX_GRID_V2 ? 'border border-blue-300' : 'border-blue-300'} cursor-not-allowed`;
                    } else if (pend === 'add') {
                      cls = `bg-amber-200 ${MATRIX_GRID_V2 ? 'border border-amber-500' : 'border-amber-500'} hover:bg-amber-100 ring-1 ring-amber-500`;
                      icon = <Plus className="w-4 h-4 text-amber-800 m-auto" />;
                    } else if (pend === 'remove') {
                      cls = `bg-rose-200 ${MATRIX_GRID_V2 ? 'border border-rose-500' : 'border-rose-500'} hover:bg-rose-100 ring-1 ring-rose-500`;
                      icon = <X className="w-4 h-4 text-rose-800 m-auto" />;
                    } else if (state === 'direct') {
                      cls = `bg-emerald-200 ${MATRIX_GRID_V2 ? 'border border-emerald-400' : 'border-emerald-400'} hover:bg-emerald-100`;
                      icon = <CheckCircle2 className="w-4 h-4 text-emerald-700 m-auto" />;
                    } else {
                      // Cella neutra: applica zebra-stripe e hover scuro
                      cls = `${zebraBg} ${borderCls} hover:bg-[#e8e8e3]`;
                    }
                    return (
                      <td
                        key={mid}
                        onClick={() => toggleCell(sku, mid)}
                        data-testid={`cell-${sku}-${mid}`}
                        className={`p-0 ${cls} cursor-pointer`}
                        style={{ width: 30, height: 30 }}
                      >
                        {icon}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Legenda + Conferma */}
      <div className="flex-none flex items-center gap-4 text-[13px] text-[#6d6d68]">
        <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-emerald-200 border border-emerald-400 rounded-sm" /> diretta</span>
        <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-blue-100 border border-blue-300 rounded-sm" /> ereditata</span>
        <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-amber-200 border border-amber-500 rounded-sm" /> da aggiungere</span>
        <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-rose-200 border border-rose-500 rounded-sm" /> da rimuovere</span>
        <div className="flex-1" />
        <span className="text-[14px] text-[#3a3a36]">
          <strong className="text-[#2b3a8f]">{pendingList.total}</strong> modifiche pendenti
        </span>
        <button
          onClick={() => pendingList.total > 0 && setConfirmOpen(true)}
          disabled={pendingList.total === 0}
          data-testid="matrix-confirm-btn"
          className="px-4 py-2 rounded-[8px] bg-[#2b3a8f] text-white text-[14px] font-semibold hover:bg-[#243279] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Conferma modifiche
        </button>
      </div>

      {confirmOpen && (
        <MatrixConfirmDialog
          pending={pendingList}
          skuMeta={skuMeta}
          machineMeta={machineMeta}
          onClose={() => setConfirmOpen(false)}
          onConfirm={onConfirm}
          submitting={submitting}
        />
      )}

      {/* Multi-select: aggiungi articoli alla matrice */}
      <MultiSelectDialog
        open={addArticlesOpen}
        onClose={() => setAddArticlesOpen(false)}
        title="Aggiungi articoli alla matrice"
        confirmLabel={(n) => n === 0 ? 'Conferma' : `Aggiungi ${n} articol${n === 1 ? 'o' : 'i'}`}
        fetcher={async ({ search, page, pageSize }) => {
          const r = await fetchArticles({ search, page, pageSize });
          return {
            total: r.total,
            items: r.items.map(a => ({
              id: a.sku, code: a.sku, brand: a.brand || '',
              primary: a.desc, secondary: a.cat || undefined,
            })),
          };
        }}
        alreadyIn={new Set(skus)}
        alreadyInLabel="già nella matrice"
        searchPlaceholder="cerca per marca, SKU o descrizione…"
        onConfirm={handleAddArticles}
        testIdPrefix="matrix-add-articles"
      />

      {/* Multi-select: aggiungi macchine alla matrice (con brand filter) */}
      <MultiSelectDialog
        open={addMachinesOpen}
        onClose={() => setAddMachinesOpen(false)}
        title="Aggiungi macchine alla matrice"
        confirmLabel={(n) => n === 0 ? 'Conferma' : `Aggiungi ${n} macchin${n === 1 ? 'a' : 'e'}`}
        fetcher={async ({ search, brand, page, pageSize }) => {
          const r = await fetchMachines({ search, brand, page, pageSize });
          return {
            total: r.total,
            items: r.items.map(m => ({
              id: m.id, code: m.id.slice(-6), brand: m.brand || '',
              primary: m.model, secondary: m.type || undefined,
            })),
          };
        }}
        alreadyIn={new Set(machineIds)}
        alreadyInLabel="già nella matrice"
        searchPlaceholder="cerca per modello o marca…"
        brandFilter={{
          value: addMachinesBrand,
          onChange: setAddMachinesBrand,
          options: marche,
          testId: 'matrix-add-machines-brand',
          placeholder: 'Tutte le marche',
        }}
        onConfirm={handleAddMachines}
        testIdPrefix="matrix-add-machines"
      />
    </div>
  );
}

// ── Sub-component: searchable async combobox per articoli ──────────

function ArticleSearchCombobox({ onPick, placeholder }: { onPick: (sku: string) => void; placeholder: string }) {
  const [q, setQ] = useState('');
  const debounced = useDebounce(q, 300);
  const [results, setResults] = useState<{ sku: string; desc: string; brand: string | null }[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!debounced || debounced.length < 2) { setResults([]); return; }
    setLoading(true);
    fetchArticles({ search: debounced, pageSize: 20 })
      .then(r => setResults(r.items.map(it => ({ sku: it.sku, desc: it.desc, brand: it.brand }))))
      .finally(() => setLoading(false));
  }, [debounced]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  return (
    <div ref={rootRef} className="relative" data-testid="matrix-article-search">
      <div className="flex items-center gap-2 px-2.5 py-[7px] border border-[#d8d8d4] rounded-[7px] bg-white">
        <Search className="w-3.5 h-3.5 text-[#5c5c57] flex-none" />
        <input
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="flex-1 text-[14px] outline-none placeholder:text-[#6d6d68] bg-transparent"
          data-testid="matrix-article-search-input"
        />
      </div>
      {open && (q.length >= 2) && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-[#d8d8d4] rounded-[9px] shadow-lg overflow-hidden">
          <div className="max-h-[280px] overflow-y-auto py-1">
            {loading ? (
              <div className="px-3 py-4 text-[14px] text-[#5c5c57] text-center">Ricerca…</div>
            ) : results.length === 0 ? (
              <div className="px-3 py-4 text-[14px] text-[#5c5c57] text-center">Nessun articolo</div>
            ) : (
              results.map(r => (
                <button
                  key={r.sku}
                  type="button"
                  onClick={() => { onPick(r.sku); setQ(''); setOpen(false); }}
                  data-testid={`matrix-article-option-${r.sku}`}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-[14px] text-left hover:bg-[#f3f3f0] text-[#3a3a36]"
                >
                  <span className="font-mono text-[13px] text-[#2b3a8f] font-semibold min-w-[60px]">{r.sku}</span>
                  <span className="flex-1 truncate">{r.desc}</span>
                  {r.brand && <span className="text-[12px] text-[#5c5c57] font-mono">{r.brand}</span>}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-component: dialog di conferma modifiche matrice ────────────

interface MatrixConfirmDialogProps {
  pending: { adds: { sku: string; mid: string }[]; removes: { sku: string; mid: string }[]; total: number };
  skuMeta: Record<string, ArticleLite>;
  machineMeta: Record<string, MachineRow>;
  onClose: () => void;
  onConfirm: () => void;
  submitting: boolean;
}

function MatrixConfirmDialog({ pending, skuMeta, machineMeta, onClose, onConfirm, submitting }: MatrixConfirmDialogProps) {
  const machineLabel = (mid: string) => {
    const m = machineMeta[mid];
    return m ? `${m.brand} ${m.model}`.trim() : mid.slice(-6);
  };
  const skuLabel = (sku: string) => {
    const m = skuMeta[sku];
    return m ? `${sku} · ${m.desc.slice(0, 40)}${m.desc.length > 40 ? '…' : ''}` : sku;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="matrix-confirm-dialog">
      <div className="bg-white rounded-[11px] shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        <div className="flex-none px-6 py-4 border-b border-[#eee]">
          <div className="text-[18px] font-semibold text-[#1f1f1d]">Conferma modifiche matrice</div>
          <div className="text-[14px] text-[#6d6d68] mt-1">
            <strong className="text-emerald-700">{pending.adds.length}</strong> aggiunte ·
            <strong className="text-rose-700 ml-1">{pending.removes.length}</strong> rimozioni
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {pending.adds.length > 0 && (
            <div>
              <div className="text-[13px] font-bold text-emerald-700 tracking-wider mb-2">DA AGGIUNGERE</div>
              <ul className="space-y-1">
                {pending.adds.map(({ sku, mid }) => (
                  <li key={`a-${sku}-${mid}`} className="text-[14px] flex items-center gap-2">
                    <Plus className="w-3 h-3 text-emerald-700 flex-none" />
                    <span className="font-mono text-[13px] text-[#2b3a8f]">{skuLabel(sku)}</span>
                    <ArrowRight className="w-3 h-3 text-[#5c5c57] flex-none" />
                    <span className="text-[#3a3a36]">{machineLabel(mid)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {pending.removes.length > 0 && (
            <div>
              <div className="text-[13px] font-bold text-rose-700 tracking-wider mb-2">DA RIMUOVERE</div>
              <ul className="space-y-1">
                {pending.removes.map(({ sku, mid }) => (
                  <li key={`r-${sku}-${mid}`} className="text-[14px] flex items-center gap-2">
                    <X className="w-3 h-3 text-rose-700 flex-none" />
                    <span className="font-mono text-[13px] text-[#2b3a8f]">{skuLabel(sku)}</span>
                    <ArrowRight className="w-3 h-3 text-[#5c5c57] flex-none" />
                    <span className="text-[#3a3a36]">{machineLabel(mid)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="flex-none px-6 py-4 border-t border-[#eee] flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            data-testid="matrix-confirm-cancel"
            className="px-4 py-2 text-[15px] text-[#5c5c57] hover:bg-[#fafafa] rounded-[8px] disabled:opacity-40"
          >
            Annulla
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            data-testid="matrix-confirm-save"
            className="px-4 py-2 rounded-[8px] bg-[#2b3a8f] text-white text-[15px] font-semibold hover:bg-[#243279] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? 'Salvataggio…' : 'Salva modifiche'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// COPY TAB — redesign secondo wireframe Abbinatore.dc.html
// ════════════════════════════════════════════════════════════════════

type CopyMode = 'machine' | 'article';

interface CopySource {
  id: string;        // sku per article, ObjectId per machine
  primary: string;
  secondary: string;
  count: number;     // associazioni esistenti
}

function CopyTab() {
  const [mode, setMode] = useState<CopyMode>('article');
  const [source, setSource] = useState<CopySource | null>(null);
  const [pickingSource, setPickingSource] = useState(false);
  const [destPickerOpen, setDestPickerOpen] = useState(false);
  const [selectedDest, setSelectedDest] = useState<{ id: string; primary: string; secondary: string }[]>([]);
  const [excludedCats, setExcludedCats] = useState<string[]>([]);
  const [categorie, setCategorie] = useState<CategoriaOption[]>([]);
  const [replace, setReplace] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Categorie che la relazione (gruppo similari / rebrand) dichiara non
  // condivise, per destinazione. Precaricate: sono regole già configurate
  // altrove, chiederle di nuovo a mano sarebbe solo un modo per sbagliarle.
  const [relEscluse, setRelEscluse] = useState<Record<string, string[]>>({});

  useEffect(() => { fetchCategorie().then(setCategorie).catch(() => {}); }, []);

  // Reset on mode change
  useEffect(() => {
    setSource(null); setSelectedDest([]); setExcludedCats([]); setRelEscluse({});
  }, [mode]);

  const destIdsKey = selectedDest.map(d => d.id).join(',');
  useEffect(() => {
    if (mode !== 'machine' || !source || selectedDest.length === 0) { setRelEscluse({}); return; }
    let cancel = false;
    fetchRelationEscluse(source.id, selectedDest.map(d => d.id))
      .then(r => { if (!cancel) setRelEscluse(r); })
      .catch(() => { if (!cancel) setRelEscluse({}); });
    return () => { cancel = true; };
  }, [mode, source?.id, destIdsKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Fetcher condiviso per sorgente/destinazioni: arricchisce con assocCount + lastAssocDate.
  // `excludeId` (opzionale) rimuove un elemento specifico dai risultati (es. il source già scelto).
  const buildFetcher = (excludeId?: string) => async ({ search, page, pageSize }: { search: string; page: number; pageSize: number }) => {
    if (mode === 'article') {
      const r = await fetchArticles({ search, page, pageSize });
      return {
        total: r.total,
        items: r.items
          .filter(a => a.sku !== excludeId)
          .map(a => ({
            id: a.sku, code: a.sku, brand: a.brand || '', primary: a.desc, secondary: a.cat || undefined,
            assocCount: a.compat, lastAssocDate: a.lastAssoc,
          })),
      };
    }
    const r = await fetchMachines({ search, page, pageSize });
    return {
      total: r.total,
      items: r.items
        .filter(m => m.id !== excludeId)
        .map(m => ({
          // `type` è un codice ERP grezzo (es. "02") senza etichetta leggibile: non mostrato.
          id: m.id, code: m.id.slice(-6), brand: m.brand || '', primary: m.model,
          assocCount: m.compat, lastAssocDate: m.lastAssoc,
        })),
    };
  };

  const sourceFetcher = useMemo(() => buildFetcher(), [mode]);
  const destFetcher = useMemo(() => buildFetcher(source?.id), [mode, source?.id]);

  const onPickSource = (item: MultiSelectItem) => {
    setSource({ id: item.id, primary: item.primary, secondary: item.secondary || '', count: item.assocCount ?? 0 });
    setPickingSource(false);
    setSelectedDest([]); setExcludedCats([]);
  };

  const handleAddDest = (items: MultiSelectItem[]) => {
    const toAdd = items.filter(it => !selectedDest.some(d => d.id === it.id));
    if (!toAdd.length) { setDestPickerOpen(false); return; }
    setSelectedDest([...selectedDest, ...toAdd.map(it => ({ id: it.id, primary: it.primary, secondary: it.secondary || '' }))]);
    setDestPickerOpen(false);
  };

  const removeDest = (id: string) => setSelectedDest(selectedDest.filter(d => d.id !== id));

  const toggleExcluded = (code: string) => {
    setExcludedCats(prev => prev.includes(code) ? prev.filter(x => x !== code) : [...prev, code]);
  };

  const onCopy = async () => {
    if (!source || selectedDest.length === 0) return;
    setSubmitting(true);
    try {
      const r = await copyAssociations({
        source_type: mode,
        source_id: source.id,
        target_ids: selectedDest.map(d => d.id),
        mode: replace ? 'replace' : 'merge',
        ...(mode === 'machine' && excludedCats.length > 0 ? { categorieEscluse: excludedCats } : {}),
      });
      toast.success(
        `${r.created} associazioni create (${replace ? 'sostituite' : 'unite'})` +
        (r.skipped_excluded ? ` · ${r.skipped_excluded} saltate per categorie escluse` : ''),
      );
      setSelectedDest([]);
    } catch (e: any) {
      toast.error('Errore: ' + (e?.message ?? 'unknown'));
    } finally {
      setSubmitting(false);
    }
  };

  const copyResult = selectedDest.length > 0
    ? `${selectedDest.length} destinazioni × ${source?.count ?? 0} = ${selectedDest.length * (source?.count ?? 0)} assoc.`
    : 'seleziona le destinazioni';

  return (
    <div className="max-w-[920px] mx-auto" data-testid="copy-tab">
      {/* Segment mode toggle */}
      <div className="flex gap-2 mb-4">
        {([
          { id: 'machine', label: 'Macchina → altre macchine' },
          { id: 'article', label: 'Articolo → altri articoli' },
        ] as { id: CopyMode; label: string }[]).map(m => {
          const active = mode === m.id;
          return (
            <button
              key={m.id}
              onClick={() => setMode(m.id)}
              data-testid={`copy-mode-${m.id}`}
              className={`px-4 py-2 rounded-[8px] text-[15px] font-semibold transition-colors border ${
                active
                  ? 'bg-[#eef1fb] border-[#2b3a8f] text-[#2b3a8f]'
                  : 'bg-white border-[#cfcfca] text-[#5c5c57] hover:bg-[#fafafa]'
              }`}
            >
              {m.label}
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-[#e2e2df] rounded-[11px] p-5">
        {mode === 'machine' && (
          <div className="mb-4 pb-4 border-b border-[#eee] flex items-center gap-2 flex-wrap" data-testid="copy-excluded-cats">
            <span className="text-[13px] font-semibold text-[#5c5c57]">Escludi altre categorie dalla copia:</span>
            {excludedCats.map(code => {
              const lbl = categorie.find(c => c.code === code)?.label ?? code;
              return (
                <span key={code} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#f7ecec] border border-[#e6cccc] rounded-[7px] text-[13px] font-semibold text-[#8a3b3b]">
                  {lbl}
                  <button
                    onClick={() => toggleExcluded(code)}
                    data-testid={`copy-excluded-remove-${code}`}
                    className="text-[#8a3b3b] hover:opacity-70"
                  >
                    ✕
                  </button>
                </span>
              );
            })}
            <CategoryPicker options={categorie} selected={excludedCats} onPick={toggleExcluded} />
            <span className="text-[12px] text-[#6d6d68] basis-full">
              Le categorie già escluse dal gruppo similari o dalla relazione rebrand vengono
              applicate da sole: le trovi sul badge di ogni destinazione.
            </span>
          </div>
        )}

        <div className="flex items-stretch gap-5">
          {/* SORGENTE */}
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-bold tracking-[0.1em] text-[#5c5c57] mb-2 font-mono">SORGENTE</div>
            {!source ? (
              <button
                onClick={() => setPickingSource(true)}
                data-testid="copy-pick-source"
                className="w-full text-left border border-dashed border-[#cfcfca] rounded-[9px] p-4 bg-[#fafafa] hover:bg-[#f3f3f0] text-[14px] text-[#5c5c57]"
              >
                Seleziona {mode === 'article' ? 'un articolo sorgente' : 'una macchina sorgente'}…
              </button>
            ) : (
              <>
                <div className="border border-[#d8d8d4] rounded-[9px] p-3.5 bg-[#fafafa]">
                  <div className="text-[16px] font-semibold text-[#1f1f1d] truncate" data-testid="copy-source-name">{source.primary}</div>
                  <div className="text-[14px] text-[#5c5c57] mt-0.5 truncate">{source.secondary}</div>
                  <div className="mt-2.5 text-[14px] font-mono font-semibold text-[#2b3a8f]">
                    {source.count} {mode === 'article' ? 'macchine compatibili' : 'articoli compatibili'}
                  </div>
                </div>
                <button
                  onClick={() => setPickingSource(true)}
                  data-testid="copy-change-source"
                  className="mt-2 text-[14px] font-medium text-[#2b3a8f] hover:underline flex items-center gap-1"
                >
                  <RotateCw className="w-3 h-3" /> Cambia sorgente
                </button>
              </>
            )}
          </div>

          {/* Arrow */}
          <div className="flex items-center justify-center flex-none w-12">
            <ArrowRight className="w-7 h-7 text-[#5c5c57]" />
          </div>

          {/* DESTINAZIONI */}
          <div className="flex-[1.4] min-w-0">
            <div className="text-[12px] font-bold tracking-[0.1em] text-[#5c5c57] font-mono mb-2">
              {mode === 'article' ? 'COPIA SU QUALI ARTICOLI' : 'COPIA SU QUALI MACCHINE'}
            </div>
            <div className="flex items-center gap-2 flex-wrap min-h-[36px]">
              {selectedDest.map(d => {
                const rel = relEscluse[d.id] ?? [];
                return (
                  <span
                    key={d.id}
                    data-testid={`copy-dest-chip-${d.id}`}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#f3f3f0] border border-[#e2e2df] rounded-[8px] text-[14px] font-semibold text-[#3a3a36] max-w-[300px]"
                  >
                    <span className="truncate">{d.primary}</span>
                    {rel.length > 0 && (
                      <CatEscluseBadge cats={rel} testId={`copy-dest-escluse-${d.id}`} />
                    )}
                    <button
                      onClick={() => removeDest(d.id)}
                      data-testid={`copy-dest-remove-${d.id}`}
                      className="text-[#5c5c57] hover:text-[#8a3b3b] flex-none"
                    >
                      ✕
                    </button>
                  </span>
                );
              })}
              <button
                onClick={() => setDestPickerOpen(true)}
                disabled={!source}
                data-testid="copy-dest-add"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-[#c4c4c1] rounded-[8px] text-[14px] text-[#5c5c57] hover:border-[#2b3a8f] hover:text-[#2b3a8f] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus className="w-3 h-3" /> {selectedDest.length === 0 ? 'scegli destinazioni…' : 'aggiungi altre'}
              </button>
            </div>
            {!source && (
              <div className="text-[13px] text-[#5c5c57] mt-2">Seleziona prima una sorgente.</div>
            )}
          </div>
        </div>

        {/* Bottom action bar */}
        <div className="mt-5 pt-4 border-t border-[#eee] flex items-center gap-4">
          <label className="flex items-center gap-2 text-[14px] text-[#5c5c57] cursor-pointer">
            <input
              type="checkbox"
              checked={replace}
              onChange={e => setReplace(e.target.checked)}
              data-testid="copy-replace"
              className="w-3.5 h-3.5"
            />
            Sostituisci associazioni esistenti <span className="text-[#5c5c57]">(default: unisci)</span>
          </label>
          <div className="flex-1" />
          <div className="text-[15px] font-semibold text-[#5c5c57]" data-testid="copy-result">{copyResult}</div>
          <button
            onClick={onCopy}
            disabled={!source || selectedDest.length === 0 || submitting}
            data-testid="copy-submit"
            className="px-4 py-2.5 rounded-[8px] bg-[#2b3a8f] text-white text-[15px] font-semibold hover:bg-[#243279] disabled:bg-[#d8d8d4] disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {submitting ? 'Copia in corso…' : <>Copia associazioni <ArrowRight className="w-3.5 h-3.5" /></>}
          </button>
        </div>
        {mode === 'machine' && (excludedCats.length > 0 || Object.keys(relEscluse).length > 0) && (
          <div className="mt-2 text-[13px] text-[#5c5c57]">
            Nota: gli articoli nelle categorie escluse non verranno copiati, quindi il numero di
            associazioni create sarà inferiore alla stima qui sopra.
          </div>
        )}
      </div>

      <MultiSelectDialog
        open={pickingSource}
        onClose={() => setPickingSource(false)}
        title={mode === 'article' ? 'Scegli articolo sorgente' : 'Scegli macchina sorgente'}
        confirmLabel={() => ''}
        fetcher={sourceFetcher}
        alreadyIn={new Set()}
        searchPlaceholder={mode === 'article' ? 'cerca per SKU o descrizione…' : 'cerca per modello o marca…'}
        onConfirm={() => {}}
        selectionMode="single"
        onPickOne={onPickSource}
        showCode={mode === 'article'}
        showAssocInfo
        testIdPrefix="copy-source"
      />

      <MultiSelectDialog
        open={destPickerOpen}
        onClose={() => setDestPickerOpen(false)}
        title={mode === 'article' ? 'Scegli articoli destinazione' : 'Scegli macchine destinazione'}
        confirmLabel={(n) => n === 0 ? 'Conferma' : `Aggiungi ${n} destinazion${n === 1 ? 'e' : 'i'}`}
        fetcher={destFetcher}
        alreadyIn={new Set(selectedDest.map(d => d.id))}
        alreadyInLabel="già selezionata"
        searchPlaceholder={mode === 'article' ? 'cerca per SKU o descrizione…' : 'cerca per modello o marca…'}
        onConfirm={handleAddDest}
        showCode={mode === 'article'}
        showAssocInfo
        testIdPrefix="copy-dest"
      />
    </div>
  );
}
